<template>
  <div>
    <LoadingOverlay :loading="loading" />
    <!-- Error state -->
    <div v-if="errorMsg" class="agency-error">
      {{ errorMsg }}
    </div>
    <div class="agency-hostList-boxes" ref="scrollBox" @scroll="handleScroll">
      <template v-if="subAagencyListData.length > 0">
        <div class="d-flex justify-content-between align-items-center hostCard-topBoxes agency-hostList-box"
          v-for="subAgencyData in subAagencyListData" :key="subAgencyData.id || subAgencyData.user_id">
          <div class="d-flex align-items-center hostCard-leftBox">
            <div class="d-flex align-items-center hostCard-imageBox">
              <img :src="subAgencyData.profile_images && subAgencyData.profile_images.length
                ? subAgencyData.profile_images[0].image_name
                : defaultProfile" alt="host profile image" @error="onImageError" />
            </div>
            <div class="hostCard-infoBox">
              <div class="hostCard-name">{{ subAgencyData.name || '-' }}</div>
              <div class="hostCard-ID">ID: {{ subAgencyData.profile_id || '-' }}</div>
            </div>
          </div>
          <div class="text-center agency-hostCountBox">
            <div class="agency-hostCount">{{ subAgencyData.hosts_count ?? 0 }}</div>
            <div class="agency-hostlabel">Host’s</div>
          </div>
        </div>
      </template>
      <!-- Empty state (only when not loading and no error) -->
      <div v-else-if="!loading && !errorMsg" class="agency-empty" aria-live="polite">
        No data
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios'
import './AgencyCentreStyle.css'
import defaultProfile from '../../assets/images/default_profile.webp'
import LoadingOverlay from '../LoadingOverlay.vue'

// ---------- Base URL (dev proxy → /api; prod → zeep.live) ----------
function withTrailingSlash(u) {
  if (!u) return '/'
  return u.endsWith('/') ? u : (u + '/')
}
const isLocal = () => ['localhost', '127.0.0.1'].includes(window.location.hostname)

const RAW_BASE =
  (import.meta?.env?.VITE_API_BASE_URL) ||
  (typeof process !== 'undefined' && process.env?.VUE_APP_API_BASE_URL) ||
  (isLocal() ? '/api' : 'https://starmate.co.in/api')

const API_BASE = withTrailingSlash(RAW_BASE)

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
})

api.interceptors.request.use((cfg) => {
  try {
    new URL(cfg.url, cfg.baseURL)
    // console.log('[API] →', (cfg.method || 'get').toUpperCase(), full.toString(), cfg.params || '')
  } catch {
    // ignore URL parse errors
   }
  return cfg
})
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.code === 'ERR_NETWORK') {
      error.userMessage = 'Can’t reach the server. Check proxy/CORS/SSL.'
    } else if (error?.response?.status === 401) {
      error.userMessage = 'Session expired. Reopen with a fresh token.'
    }
    return Promise.reject(error)
  }
)
// -------------------------------------------------------------------

export default {
  name: 'SubAgencyListTabCont',
  components: { LoadingOverlay },
  data() {
    return {
      subAagencyListData: [],
      defaultProfile,
      loading: false,
      currentPage: 1,
      nextPageUrl: null,
      noMoreData: false,
      authToken: '',
      errorMsg: '',
    }
  },
  async mounted() {
    // Read token from /sub-agency-list/token=<JWT> or ?token=<JWT>
    const raw = this.$route.params.token || this.$route.query.token || ''
    this.authToken = this.normalizeToken(raw)

    if (!this.authToken) {
      this.errorMsg = 'JWT token missing in URL.'
      console.error('JWT token missing. Expected /.../token=<JWT> or ?token=<JWT>')
      return
    }

    // Use token for all requests (backend infers user/agency from token)
    api.defaults.headers.common.Authorization = `Bearer ${this.authToken}`

    await this.fetchSubAagencyListData()
  },
  methods: {
    normalizeToken(v) {
      if (!v || typeof v !== 'string') return ''
      return v.startsWith('token=') ? v.slice(6) : v
    },

    async fetchSubAagencyListData() {
      if (this.loading || this.noMoreData) return
      this.loading = true
      try {
        // ✅ No userid here — token handles identity
        const { data } = await api.get('subagencylistfromagency', {
          params: { page: this.currentPage },
        })

        const result = data?.result
        if (!result || !result.data) {
          this.noMoreData = true
          return
        }

        const { data: rows, next_page_url } = result

        if (Array.isArray(rows) && rows.length > 0) {
          this.subAagencyListData.push(...rows)
          this.currentPage++
          this.nextPageUrl = next_page_url
        }

        if (!next_page_url || !rows?.length) {
          this.noMoreData = true
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        // Stop trying on 401/403
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          this.errorMsg = error?.userMessage || 'Unauthorized. Please check your token.'
          this.noMoreData = true
        } else if (error?.userMessage) {
          this.errorMsg = error.userMessage
        } else {
          this.errorMsg = 'Something went wrong while fetching data.'
        }
      } finally {
        this.loading = false
      }
    },

    onImageError(e) {
      e.target.src = this.defaultProfile
    },

    handleScroll() {
      const box = this.$refs.scrollBox
      if (!box) return
      if (box.scrollTop + box.clientHeight >= box.scrollHeight - 5) {
        if (!this.noMoreData) this.fetchSubAagencyListData()
      }
    },
  },
}
</script>
